import React, { useEffect, useState } from 'react';
import Icon from 'components/AppIcon';

/*
  Usage:
  <ContentStateIndicator
    status="generating" | "success" | "error" | "idle"
    progress={0-100}
    label="Generating your game..."
    onDismiss={() => {}}
  />
*/

const STATUS_CONFIG = {
  generating: {
    icon: 'Cpu',
    badgeClass: 'generating',
    iconColor: 'var(--color-primary)',
    label: 'Generating...',
  },
  success: {
    icon: 'CheckCircle2',
    badgeClass: 'success',
    iconColor: 'var(--color-success)',
    label: 'Generation complete!',
  },
  error: {
    icon: 'AlertCircle',
    badgeClass: 'error',
    iconColor: 'var(--color-error)',
    label: 'Generation failed',
  },
  idle: null,
};

export default function ContentStateIndicator({
  status = 'idle',
  progress = 0,
  label,
  estimatedTime,
  onDismiss,
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (status !== 'idle') {
      setVisible(true);
    }
    // Auto-dismiss success after 4s
    if (status === 'success') {
      const timer = setTimeout(() => {
        setVisible(false);
        onDismiss?.();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [status, onDismiss]);

  if (!visible || status === 'idle') return null;

  const config = STATUS_CONFIG?.[status];
  if (!config) return null;

  const displayLabel = label || config?.label;

  return (
    <div
      className="content-state-bar"
      role="status"
      aria-live="polite"
      aria-label={displayLabel}
    >
      {/* Status badge */}
      <div className={`content-state-badge ${config?.badgeClass}`}>
        <Icon
          name={config?.icon}
          size={14}
          color={config?.iconColor}
          className={status === 'generating' ? 'pulse-glow' : ''}
        />
        <span>{displayLabel}</span>
      </div>
      {/* Progress bar - only for generating */}
      {status === 'generating' && (
        <div className="content-state-progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <div
            className="content-state-progress-fill"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      )}
      {/* Estimated time */}
      {status === 'generating' && estimatedTime && (
        <span className="content-state-label">
          ~{estimatedTime}s remaining
        </span>
      )}
      {/* Progress percentage */}
      {status === 'generating' && (
        <span className="content-state-label font-data" style={{ fontFamily: 'var(--font-data)', minWidth: 36 }}>
          {Math.round(progress)}%
        </span>
      )}
      {/* Dismiss button */}
      {(status === 'success' || status === 'error') && onDismiss && (
        <button
          onClick={() => {
            setVisible(false);
            onDismiss();
          }}
          className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-muted transition-all duration-250 ml-auto"
          aria-label="Dismiss notification"
          style={{ color: 'var(--color-muted-foreground)', flexShrink: 0 }}
        >
          <Icon name="X" size={14} />
        </button>
      )}
    </div>
  );
}